/**
 * PIEUnseededEmptyState — rendered across every PIE page when the
 * backend reports "No PIE user found for this caller". Tells the user
 * the tenant has never been seeded and routes them to the right next
 * action based on their role.
 *
 * Shared by Dashboard / Improve / Simulator / Coach / Validate so that
 * deep-linking into any sub-page on a fresh deployment shows the same
 * polished empty state instead of a raw error string.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Users, ArrowRight, Sparkles, ShieldAlert } from 'lucide-react';
import useModules from '../../lib/useModules';

/** True when a server error string signals "tenant not seeded yet". */
export function isUnseededError(err) {
  return typeof err === 'string' && /no pie user found/i.test(err);
}

export default function PIEUnseededEmptyState() {
  const { isSuperAdmin, loading } = useModules();
  return (
    <div
      className="pie-card pie-card-elevated"
      style={{
        marginTop: 32, padding: '40px 32px', textAlign: 'center',
        maxWidth: 640, marginLeft: 'auto', marginRight: 'auto',
      }}
      data-testid="pie-unseeded-empty"
    >
      <div style={{
        width: 56, height: 56, borderRadius: 14, margin: '0 auto 18px',
        background: 'rgba(124, 92, 255, 0.12)', color: 'var(--pie-accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Users size={26} aria-hidden />
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px', color: 'var(--pie-text)' }}>
        Your tenant hasn&apos;t been seeded yet
      </h2>
      <p
        style={{
          fontSize: 14, color: 'var(--pie-text-secondary)', lineHeight: 1.6,
          margin: '0 0 24px', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto',
        }}
      >
        Seertech Mirror needs a synthetic population (200 users + 5 showcase personas)
        before any of the dashboards can render. This is a one-time setup per environment.
      </p>

      {loading ? (
        <div style={{ fontSize: 13, color: 'var(--pie-text-muted)' }}>Checking your role…</div>
      ) : isSuperAdmin ? (
        <Link
          to="/admin/data-health"
          className="pie-btn pie-btn-primary"
          data-testid="pie-unseeded-go-to-data-health"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Sparkles size={14} /> Open Data Health → Seed PIE personas
          <ArrowRight size={14} />
        </Link>
      ) : (
        <div
          style={{
            display: 'inline-flex', alignItems: 'flex-start', gap: 12,
            padding: '14px 18px', borderRadius: 10,
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            color: 'var(--pie-text)', fontSize: 13, lineHeight: 1.6, textAlign: 'left',
            maxWidth: 480,
          }}
        >
          <ShieldAlert size={18} style={{ color: '#D97706', flexShrink: 0, marginTop: 1 }} aria-hidden />
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Ask your super admin</div>
            <div style={{ color: 'var(--pie-text-secondary)' }}>
              They&apos;ll need to open <strong>Administration → Data Health</strong> and click
              <strong> &quot;Seed PIE Personas&quot;</strong>. It takes about 5 seconds.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
